import { z } from "zod";

import { AllowedFileFormats } from "../../../shared/domain/chat/chat.types";

const AllowedFileFormatsSchema = z.nativeEnum(AllowedFileFormats);

/**
 * Skill types that can be enabled for a deployment.
 * These match the SkillsTypes enum from deployment.types.ts
 */
export const SkillTypeSchema = z.enum([
  "TASK",
  "TOOL",
  "EXTERNAL_TOOL",
  "KNOWER",
  "FLOW",
  "IMAGE_GENERATION"
]);

export type SkillType = z.infer<typeof SkillTypeSchema>;

/**
 * Schema for a single enabled skill reference.
 * Skills are stored by ID and type for flexibility.
 */
export const EnabledSkillSchema = z.object({
  id: z.string().uuid(),
  type: SkillTypeSchema
});

export type EnabledSkill = z.infer<typeof EnabledSkillSchema>;

/**
 * Schema for document handling task configuration.
 * Defines which tasks can process uploaded documents.
 */
export const DocumentTaskSchema = z.object({
  /** Task identifier */
  id: z.string().uuid(),
  /** Human-readable action name */
  action: z.string().min(1).max(100)
});

export type DocumentTask = z.infer<typeof DocumentTaskSchema>;

/**
 * Document configuration modes.
 * - ALL: Accept all supported file types
 * - MAX_LENGTH: Limit by file size
 * - SKILLS: Only accept files for configured skills
 */
export const DocumentConfigTypeSchema = z.enum(["ALL", "MAX_LENGTH", "SKILLS"]);

export type DocumentConfigType = z.infer<typeof DocumentConfigTypeSchema>;

/**
 * Assistant behavior settings - SHARED across all deployment types.
 *
 * These settings control how the AI assistant behaves:
 * - Document handling
 * - Enabled skills
 *
 * Used by: Email, Telegram, Embed, PhoneCall, etc.
 */
export const AssistantSettingsSchema = z.object({
  /**
   * File formats users can upload.
   * Empty array = no file uploads allowed.
   */
  sendDocumentsType: z.array(AllowedFileFormatsSchema).default([]),

  /**
   * Tasks that can process uploaded documents.
   * Associates uploaded files with specific processing tasks.
   */
  sendDocumentsTasksEnabled: z.array(DocumentTaskSchema).default([]),

  /**
   * How document handling is configured.
   * @see DocumentConfigTypeSchema for options
   */
  sendDocumentsConfigType: DocumentConfigTypeSchema.default("ALL"),

  // ---------------------------------------------------------
  // DEPRECATED FIELDS - Kept for backwards compatibility
  // These are always enabled/defaulted in the backend now
  // ---------------------------------------------------------

  /** @deprecated Always "Auto" - kept for schema compatibility */
  chatDefaultCommand: z.string().optional(),

  /** @deprecated All skills available - kept for schema compatibility */
  enabledSkills: z.array(EnabledSkillSchema).optional(),

  /** @deprecated Always enabled - kept for schema compatibility */
  enableKnowledges: z.boolean().optional(),

  /** @deprecated Always enabled - kept for schema compatibility */
  enableInternetSearch: z.boolean().optional(),

  /** @deprecated Use sendDocumentsType - kept for schema compatibility */
  enableSendDocuments: z.boolean().optional()
});

export type AssistantSettings = z.infer<typeof AssistantSettingsSchema>;

/**
 * Default assistant settings for new deployments.
 * Used when creating deployments without explicit settings.
 */
export const DEFAULT_ASSISTANT_SETTINGS: AssistantSettings = {
  sendDocumentsType: [],
  sendDocumentsTasksEnabled: [],
  sendDocumentsConfigType: "ALL"
};
